// The three statuses an intel check reads, in the order the server-rendered "How this role
// reads" table uses. The key is the attribution field the server names the responsible
// modifier under.
const statuses = [
    {
        key: 'viewedAlignment',
        label: 'A Seer sees',
        value: (profile) => profile.viewedAlignment,
        card: (profile) => profile.viewedAlignmentCard,
    },
    {
        key: 'viewedAsKilling',
        label: 'Killing role',
        value: (profile) => (profile.viewedAsKilling ? 'Yes, seen as a killer' : 'No'),
        card: (profile) => profile.killingCard,
    },
    {
        key: 'usesWitchcraft',
        label: 'Witchcraft',
        value: (profile) => (profile.usesWitchcraft ? 'Yes, uses witchcraft' : 'No'),
        card: (profile) => profile.witchcraftCard,
    },
];

function causeFor(attribution, key) {
    if (!attribution) return null;
    return attribution[key] || null;
}

// The bearer half is compared against the bearer baseline rather than the intel one: a role
// whose believed role differs already reads differently to itself before any modifier, and
// flagging that as "the loadout told you something" would be wrong.
export function rowsFor(effects, readings) {
    if (!effects || !readings || !readings.intel || !readings.bearer) return [];

    return statuses.map((status) => {
        const before = status.value(effects.intelBaseline);
        const after = status.value(readings.intel);
        const beforeCard = status.card(effects.intelBaseline);
        const afterCard = status.card(readings.intel);
        const bearerBefore = status.value(effects.bearerBaseline);
        const bearerAfter = status.value(readings.bearer);

        return {
            key: status.key,
            label: status.label,
            before,
            after,
            beforeCard,
            afterCard,
            bearerBefore,
            bearerAfter,
            changed: before !== after || beforeCard !== afterCard,
            cardChanged: beforeCard !== afterCard,
            bearerChanged: bearerBefore !== bearerAfter,
            cause: causeFor(readings.intelAttribution, status.key),
            bearerCause: causeFor(readings.bearerAttribution, status.key),
        };
    });
}

// Slugs are unique within a section, not across them, so an item and an aura sharing a name
// would collide on slug alone.
export function optionKey(option) {
    return `${option.kind}:${option.slug}`;
}

// The whole published catalogue is on offer, so the menu leads with the modifiers the artefact
// knows change this role by themselves. That is a hint about where to start, not a restriction:
// a modifier that changes nothing alone can still decide a reading in company.
export function menuGroupsFor(effects, loadout, filter, roleName) {
    if (!effects) return [];

    const taken = new Set((loadout || []).map(optionKey));
    const term = (filter || '').trim().toLowerCase();
    const available = (option) =>
        !taken.has(optionKey(option)) &&
        (term === '' || option.displayName.toLowerCase().includes(term));

    const items = (effects.items || []).filter(available);
    const auras = (effects.auras || []).filter(available);

    return [
        {
            label: `Items that change the ${roleName} on their own`,
            options: items.filter((option) => option.changesThisRole),
        },
        { label: 'Other items', options: items.filter((option) => !option.changesThisRole) },
        {
            label: `Auras that change the ${roleName} on their own`,
            options: auras.filter((option) => option.changesThisRole),
        },
        { label: 'Other auras', options: auras.filter((option) => !option.changesThisRole) },
    ].filter((group) => group.options.length > 0);
}

export function findOption(effects, key) {
    if (!effects) return null;

    const all = (effects.items || []).concat(effects.auras || []);
    return all.find((option) => optionKey(option) === key) || null;
}

// The readings for an empty loadout are the role's own, so an empty picker renders the same
// table without asking the server anything.
export function baselineReadings(effects) {
    if (!effects) return null;

    return {
        intel: effects.intelBaseline,
        bearer: effects.bearerBaseline,
        intelAttribution: null,
        bearerAttribution: null,
        unsimulated: [],
        auras: effects.aurasBaseline || [],
    };
}

// Split rather than filtered twice in the template, so the two groups cannot disagree about
// which auras exist. The server has already ordered them; preserving that order keeps the same
// loadout rendering the same way every time.
export function auraGroupsFor(readings) {
    const auras = (readings && readings.auras) || [];

    return {
        visible: auras.filter((aura) => aura.knownToBearer),
        hidden: auras.filter((aura) => !aura.knownToBearer),
    };
}

export default rowsFor;
